import { useMemo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TimelinessChart } from './TimelinessChart';
import type { AnnexData, AnnexKey, Psmf } from './types';
import { ANNEX_TITLES } from './types';

type Updater = (key: AnnexKey, updater: (prev: AnnexData[AnnexKey]) => AnnexData[AnnexKey], fieldLabel: string) => void;

interface Props {
  annexKey: AnnexKey;
  psmf: Psmf;
  onAnnexChange: Updater;
  onFindingStatus?: 'pass' | 'fail' | 'unknown';
}

function newRowId(): string {
  return Math.random().toString(36).slice(2, 10);
}

export function AnnexTab({ annexKey, psmf, onAnnexChange, onFindingStatus }: Props) {
  const node = psmf.annex[annexKey];
  const title = ANNEX_TITLES[annexKey];
  const unused = node.unused;

  const toggleUnused = () => {
    onAnnexChange(annexKey, (prev) => ({ ...prev, unused: !prev.unused }), `Annex ${annexKey} unused flag`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold">Annex {annexKey} — {title}{unused ? ' — unused' : ''}</h2>
            {onFindingStatus === 'pass' && <Badge variant="success">OK</Badge>}
            {onFindingStatus === 'fail' && <Badge variant="destructive">Gap</Badge>}
          </div>
          <p className="text-xs text-muted-foreground">
            GVP Module II Section II.B.6.1. Annex letters are fixed; if absent, mark "unused" rather than renaming.
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={unused}
            onChange={toggleUnused}
            className="h-4 w-4 rounded border-input"
          />
          Mark as unused
        </label>
      </div>

      {unused ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            Annex {annexKey} is marked "unused". The tab remains labelled "Annex {annexKey} — unused"
            in the index; it will not be renumbered. Uncheck "Mark as unused" to populate it.
          </CardContent>
        </Card>
      ) : (
        <AnnexBody annexKey={annexKey} psmf={psmf} onAnnexChange={onAnnexChange} />
      )}
    </div>
  );
}

function AnnexBody({ annexKey, psmf, onAnnexChange }: { annexKey: AnnexKey; psmf: Psmf; onAnnexChange: Updater }) {
  switch (annexKey) {
    case 'A': return <AnnexA psmf={psmf} onAnnexChange={onAnnexChange} />;
    case 'B': return <AnnexB psmf={psmf} onAnnexChange={onAnnexChange} />;
    case 'C': return <AnnexC psmf={psmf} onAnnexChange={onAnnexChange} />;
    case 'D': return <AnnexD psmf={psmf} onAnnexChange={onAnnexChange} />;
    case 'E': return <AnnexE psmf={psmf} onAnnexChange={onAnnexChange} />;
    case 'F': return <AnnexF psmf={psmf} onAnnexChange={onAnnexChange} />;
    case 'G': return <AnnexG psmf={psmf} onAnnexChange={onAnnexChange} />;
    case 'H': return <AnnexH psmf={psmf} onAnnexChange={onAnnexChange} />;
    case 'I': return <AnnexI psmf={psmf} onAnnexChange={onAnnexChange} />;
  }
}

// ---------- Annex A: QPPV ----------
function AnnexA({ psmf, onAnnexChange }: { psmf: Psmf; onAnnexChange: Updater }) {
  const a = psmf.annex.A;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">QPPV curriculum vitae</CardTitle></CardHeader>
        <CardContent>
          <Label htmlFor="cv">CV reference / storage location (upload TBD)</Label>
          <Input
            id="cv"
            value={a.cvReference}
            onChange={(e) => onAnnexChange('A', (p) => ({ ...p, cvReference: e.target.value }) as typeof a, 'Annex A — CV reference')}
            placeholder="e.g. SharePoint: /QA/QPPV/CV-Smith-v3.pdf"
          />
          <p className="mt-2 text-xs text-muted-foreground">
            File upload placeholder — back-end endpoint pending. Reference the authoritative location for now.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Delegated tasks</CardTitle>
          <Button
            size="sm"
            onClick={() => onAnnexChange('A', (p) => {
              const cur = p as typeof a;
              return { ...cur, delegations: [...cur.delegations, { id: newRowId(), task: '', delegatedTo: '', oversight: '' }] };
            }, 'Annex A — added delegation')}
          >
            <Plus className="mr-1 h-4 w-4" /> Add row
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Task</TableHead>
                <TableHead>Delegated to</TableHead>
                <TableHead>Oversight</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {a.delegations.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground">No delegations listed.</TableCell></TableRow>
              )}
              {a.delegations.map((d, idx) => (
                <TableRow key={d.id}>
                  <TableCell>
                    <Input value={d.task} onChange={(e) => onAnnexChange('A', (p) => {
                      const cur = p as typeof a;
                      const next = [...cur.delegations];
                      next[idx] = { ...d, task: e.target.value };
                      return { ...cur, delegations: next };
                    }, 'Annex A — delegation task')} />
                  </TableCell>
                  <TableCell>
                    <Input value={d.delegatedTo} onChange={(e) => onAnnexChange('A', (p) => {
                      const cur = p as typeof a;
                      const next = [...cur.delegations];
                      next[idx] = { ...d, delegatedTo: e.target.value };
                      return { ...cur, delegations: next };
                    }, 'Annex A — delegatee')} />
                  </TableCell>
                  <TableCell>
                    <Input value={d.oversight} onChange={(e) => onAnnexChange('A', (p) => {
                      const cur = p as typeof a;
                      const next = [...cur.delegations];
                      next[idx] = { ...d, oversight: e.target.value };
                      return { ...cur, delegations: next };
                    }, 'Annex A — oversight')} />
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onAnnexChange('A', (p) => {
                        const cur = p as typeof a;
                        return { ...cur, delegations: cur.delegations.filter((x) => x.id !== d.id) };
                      }, 'Annex A — removed delegation')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Supplementary QPPV contacts</CardTitle>
          <Button
            size="sm"
            onClick={() => onAnnexChange('A', (p) => {
              const cur = p as typeof a;
              return { ...cur, contacts: [...cur.contacts, { id: newRowId(), name: '', role: '', email: '', phone: '' }] };
            }, 'Annex A — added contact')}
          >
            <Plus className="mr-1 h-4 w-4" /> Add contact
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {a.contacts.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">No contacts listed.</TableCell></TableRow>
              )}
              {a.contacts.map((c, idx) => (
                <TableRow key={c.id}>
                  <TableCell><Input value={c.name} onChange={(e) => onAnnexChange('A', (p) => { const cur = p as typeof a; const next = [...cur.contacts]; next[idx] = { ...c, name: e.target.value }; return { ...cur, contacts: next }; }, 'Annex A — contact name')} /></TableCell>
                  <TableCell><Input value={c.role} onChange={(e) => onAnnexChange('A', (p) => { const cur = p as typeof a; const next = [...cur.contacts]; next[idx] = { ...c, role: e.target.value }; return { ...cur, contacts: next }; }, 'Annex A — contact role')} /></TableCell>
                  <TableCell><Input value={c.email} onChange={(e) => onAnnexChange('A', (p) => { const cur = p as typeof a; const next = [...cur.contacts]; next[idx] = { ...c, email: e.target.value }; return { ...cur, contacts: next }; }, 'Annex A — contact email')} /></TableCell>
                  <TableCell><Input value={c.phone} onChange={(e) => onAnnexChange('A', (p) => { const cur = p as typeof a; const next = [...cur.contacts]; next[idx] = { ...c, phone: e.target.value }; return { ...cur, contacts: next }; }, 'Annex A — contact phone')} /></TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => onAnnexChange('A', (p) => { const cur = p as typeof a; return { ...cur, contacts: cur.contacts.filter((x) => x.id !== c.id) }; }, 'Annex A — removed contact')}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Annex B: Contracts ----------
function AnnexB({ psmf, onAnnexChange }: { psmf: Psmf; onAnnexChange: Updater }) {
  const b = psmf.annex.B;
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Contracts and agreements</CardTitle>
        <Button
          size="sm"
          onClick={() => onAnnexChange('B', (p) => {
            const cur = p as typeof b;
            return { ...cur, contracts: [...cur.contracts, { id: newRowId(), counterparty: '', type: 'SDEA', scope: '', effectiveDate: '', status: 'active' }] };
          }, 'Annex B — added contract')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add contract
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Counterparty</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Effective</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {b.contracts.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">No contracts listed.</TableCell></TableRow>
            )}
            {b.contracts.map((c, idx) => (
              <TableRow key={c.id}>
                <TableCell><Input value={c.counterparty} onChange={(e) => onAnnexChange('B', (p) => { const cur = p as typeof b; const next = [...cur.contracts]; next[idx] = { ...c, counterparty: e.target.value }; return { ...cur, contracts: next }; }, 'Annex B — counterparty')} /></TableCell>
                <TableCell>
                  <select
                    value={c.type}
                    onChange={(e) => onAnnexChange('B', (p) => { const cur = p as typeof b; const next = [...cur.contracts]; next[idx] = { ...c, type: e.target.value }; return { ...cur, contracts: next }; }, 'Annex B — contract type')}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option>SDEA</option>
                    <option>MSA</option>
                    <option>Sublicensing</option>
                    <option>CRO</option>
                    <option>Distributor</option>
                    <option>Other</option>
                  </select>
                </TableCell>
                <TableCell><Input value={c.scope} onChange={(e) => onAnnexChange('B', (p) => { const cur = p as typeof b; const next = [...cur.contracts]; next[idx] = { ...c, scope: e.target.value }; return { ...cur, contracts: next }; }, 'Annex B — scope')} /></TableCell>
                <TableCell><Input type="date" value={c.effectiveDate} onChange={(e) => onAnnexChange('B', (p) => { const cur = p as typeof b; const next = [...cur.contracts]; next[idx] = { ...c, effectiveDate: e.target.value }; return { ...cur, contracts: next }; }, 'Annex B — effective date')} /></TableCell>
                <TableCell>
                  <select
                    value={c.status}
                    onChange={(e) => onAnnexChange('B', (p) => { const cur = p as typeof b; const next = [...cur.contracts]; next[idx] = { ...c, status: e.target.value }; return { ...cur, contracts: next }; }, 'Annex B — status')}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option>active</option>
                    <option>expired</option>
                    <option>terminated</option>
                  </select>
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => onAnnexChange('B', (p) => { const cur = p as typeof b; return { ...cur, contracts: cur.contracts.filter((x) => x.id !== c.id) }; }, 'Annex B — removed contract')}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ---------- Annex C: Sources ----------
function AnnexC({ psmf, onAnnexChange }: { psmf: Psmf; onAnnexChange: Updater }) {
  const c = psmf.annex.C;
  const fiveYearAgo = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 5);
    return d.toISOString().slice(0, 10);
  }, []);
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Sources of safety data (rolling 5-year window)</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Include affiliates, third parties, studies and programmes active on or after {fiveYearAgo}.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => onAnnexChange('C', (p) => {
            const cur = p as typeof c;
            return { ...cur, sources: [...cur.sources, { id: newRowId(), kind: 'affiliate', name: '', country: '', startDate: '', endDate: '' }] };
          }, 'Annex C — added source')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add source
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kind</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Start</TableHead>
              <TableHead>End</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {c.sources.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">No sources listed.</TableCell></TableRow>
            )}
            {c.sources.map((s, idx) => (
              <TableRow key={s.id}>
                <TableCell>
                  <select
                    value={s.kind}
                    onChange={(e) => onAnnexChange('C', (p) => { const cur = p as typeof c; const next = [...cur.sources]; next[idx] = { ...s, kind: e.target.value }; return { ...cur, sources: next }; }, 'Annex C — kind')}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option>affiliate</option>
                    <option>study</option>
                    <option>programme</option>
                    <option>third-party</option>
                  </select>
                </TableCell>
                <TableCell><Input value={s.name} onChange={(e) => onAnnexChange('C', (p) => { const cur = p as typeof c; const next = [...cur.sources]; next[idx] = { ...s, name: e.target.value }; return { ...cur, sources: next }; }, 'Annex C — name')} /></TableCell>
                <TableCell><Input value={s.country} onChange={(e) => onAnnexChange('C', (p) => { const cur = p as typeof c; const next = [...cur.sources]; next[idx] = { ...s, country: e.target.value }; return { ...cur, sources: next }; }, 'Annex C — country')} /></TableCell>
                <TableCell><Input type="date" value={s.startDate} onChange={(e) => onAnnexChange('C', (p) => { const cur = p as typeof c; const next = [...cur.sources]; next[idx] = { ...s, startDate: e.target.value }; return { ...cur, sources: next }; }, 'Annex C — start')} /></TableCell>
                <TableCell><Input type="date" value={s.endDate} onChange={(e) => onAnnexChange('C', (p) => { const cur = p as typeof c; const next = [...cur.sources]; next[idx] = { ...s, endDate: e.target.value }; return { ...cur, sources: next }; }, 'Annex C — end')} /></TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => onAnnexChange('C', (p) => { const cur = p as typeof c; return { ...cur, sources: cur.sources.filter((x) => x.id !== s.id) }; }, 'Annex C — removed source')}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ---------- Annex D: Systems ----------
function AnnexD({ psmf, onAnnexChange }: { psmf: Psmf; onAnnexChange: Updater }) {
  const d = psmf.annex.D;
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Computerised systems and databases</CardTitle>
        <Button
          size="sm"
          onClick={() => onAnnexChange('D', (p) => {
            const cur = p as typeof d;
            return { ...cur, systems: [...cur.systems, { id: newRowId(), name: '', version: '', location: '', operationalOwner: '', fitnessForPurpose: '', validationStatus: '' }] };
          }, 'Annex D — added system')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add system
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Validation</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {d.systems.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">No systems listed.</TableCell></TableRow>
            )}
            {d.systems.map((s, idx) => (
              <TableRow key={s.id}>
                <TableCell><Input value={s.name} onChange={(e) => onAnnexChange('D', (p) => { const cur = p as typeof d; const next = [...cur.systems]; next[idx] = { ...s, name: e.target.value }; return { ...cur, systems: next }; }, 'Annex D — system name')} /></TableCell>
                <TableCell><Input value={s.version} onChange={(e) => onAnnexChange('D', (p) => { const cur = p as typeof d; const next = [...cur.systems]; next[idx] = { ...s, version: e.target.value }; return { ...cur, systems: next }; }, 'Annex D — system version')} /></TableCell>
                <TableCell><Input value={s.location} onChange={(e) => onAnnexChange('D', (p) => { const cur = p as typeof d; const next = [...cur.systems]; next[idx] = { ...s, location: e.target.value }; return { ...cur, systems: next }; }, 'Annex D — system location')} /></TableCell>
                <TableCell><Input value={s.operationalOwner} onChange={(e) => onAnnexChange('D', (p) => { const cur = p as typeof d; const next = [...cur.systems]; next[idx] = { ...s, operationalOwner: e.target.value }; return { ...cur, systems: next }; }, 'Annex D — system owner')} /></TableCell>
                <TableCell><Input value={s.validationStatus} onChange={(e) => onAnnexChange('D', (p) => { const cur = p as typeof d; const next = [...cur.systems]; next[idx] = { ...s, validationStatus: e.target.value }; return { ...cur, systems: next }; }, 'Annex D — validation status')} placeholder="GAMP 5 IQ/OQ/PQ" /></TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => onAnnexChange('D', (p) => { const cur = p as typeof d; return { ...cur, systems: cur.systems.filter((x) => x.id !== s.id) }; }, 'Annex D — removed system')}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ---------- Annex E: SOPs ----------
function AnnexE({ psmf, onAnnexChange }: { psmf: Psmf; onAnnexChange: Updater }) {
  const e = psmf.annex.E;
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">PV procedural documents</CardTitle>
        <Button
          size="sm"
          onClick={() => onAnnexChange('E', (p) => {
            const cur = p as typeof e;
            return { ...cur, sops: [...cur.sops, { id: newRowId(), docId: '', title: '', effectiveDate: '', docType: 'SOP' }] };
          }, 'Annex E — added SOP')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add procedure
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Doc ID</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Effective date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {e.sops.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">No procedures listed.</TableCell></TableRow>
            )}
            {e.sops.map((s, idx) => (
              <TableRow key={s.id}>
                <TableCell><Input value={s.docId} onChange={(ev) => onAnnexChange('E', (p) => { const cur = p as typeof e; const next = [...cur.sops]; next[idx] = { ...s, docId: ev.target.value }; return { ...cur, sops: next }; }, 'Annex E — doc id')} placeholder="SOP-PV-001" /></TableCell>
                <TableCell><Input value={s.title} onChange={(ev) => onAnnexChange('E', (p) => { const cur = p as typeof e; const next = [...cur.sops]; next[idx] = { ...s, title: ev.target.value }; return { ...cur, sops: next }; }, 'Annex E — title')} /></TableCell>
                <TableCell><Input type="date" value={s.effectiveDate} onChange={(ev) => onAnnexChange('E', (p) => { const cur = p as typeof e; const next = [...cur.sops]; next[idx] = { ...s, effectiveDate: ev.target.value }; return { ...cur, sops: next }; }, 'Annex E — effective date')} /></TableCell>
                <TableCell>
                  <select
                    value={s.docType}
                    onChange={(ev) => onAnnexChange('E', (p) => { const cur = p as typeof e; const next = [...cur.sops]; next[idx] = { ...s, docType: ev.target.value }; return { ...cur, sops: next }; }, 'Annex E — doc type')}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option>SOP</option>
                    <option>Work Instruction</option>
                    <option>Manual</option>
                    <option>Policy</option>
                  </select>
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => onAnnexChange('E', (p) => { const cur = p as typeof e; return { ...cur, sops: cur.sops.filter((x) => x.id !== s.id) }; }, 'Annex E — removed SOP')}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ---------- Annex F: KPIs + timeliness ----------
function AnnexF({ psmf, onAnnexChange }: { psmf: Psmf; onAnnexChange: Updater }) {
  const f = psmf.annex.F;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Performance indicators</CardTitle>
          <Button
            size="sm"
            onClick={() => onAnnexChange('F', (p) => {
              const cur = p as typeof f;
              return { ...cur, kpis: [...cur.kpis, { id: newRowId(), name: '', target: '', currentResult: '' }] };
            }, 'Annex F — added KPI')}
          >
            <Plus className="mr-1 h-4 w-4" /> Add KPI
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Indicator</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Current result</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {f.kpis.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground">No KPIs listed.</TableCell></TableRow>
              )}
              {f.kpis.map((k, idx) => (
                <TableRow key={k.id}>
                  <TableCell><Input value={k.name} onChange={(e) => onAnnexChange('F', (p) => { const cur = p as typeof f; const next = [...cur.kpis]; next[idx] = { ...k, name: e.target.value }; return { ...cur, kpis: next }; }, 'Annex F — KPI name')} placeholder="15-day expedited ICSR on-time %" /></TableCell>
                  <TableCell><Input value={k.target} onChange={(e) => onAnnexChange('F', (p) => { const cur = p as typeof f; const next = [...cur.kpis]; next[idx] = { ...k, target: e.target.value }; return { ...cur, kpis: next }; }, 'Annex F — KPI target')} placeholder=">= 95%" /></TableCell>
                  <TableCell><Input value={k.currentResult} onChange={(e) => onAnnexChange('F', (p) => { const cur = p as typeof f; const next = [...cur.kpis]; next[idx] = { ...k, currentResult: e.target.value }; return { ...cur, kpis: next }; }, 'Annex F — KPI result')} /></TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => onAnnexChange('F', (p) => { const cur = p as typeof f; return { ...cur, kpis: cur.kpis.filter((x) => x.id !== k.id) }; }, 'Annex F — removed KPI')}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <TimelinessChart
            label="15-day expedited ICSR reporting"
            reference="GVP II Section II.B.4.6"
            days={15}
            commentary={f.timeliness15Day || undefined}
          />
          <Label htmlFor="t15">Commentary — 15-day timeliness</Label>
          <Textarea
            id="t15"
            rows={3}
            value={f.timeliness15Day}
            onChange={(e) => onAnnexChange('F', (p) => ({ ...(p as typeof f), timeliness15Day: e.target.value }), 'Annex F — 15-day commentary')}
            placeholder="Summarise how the 15-day deadline is met and any outliers."
          />
        </div>
        <div className="space-y-2">
          <TimelinessChart
            label="90-day non-expedited ICSR reporting"
            reference="GVP II Section II.B.4.6"
            days={90}
            commentary={f.timeliness90Day || undefined}
          />
          <Label htmlFor="t90">Commentary — 90-day timeliness</Label>
          <Textarea
            id="t90"
            rows={3}
            value={f.timeliness90Day}
            onChange={(e) => onAnnexChange('F', (p) => ({ ...(p as typeof f), timeliness90Day: e.target.value }), 'Annex F — 90-day commentary')}
          />
        </div>
      </div>
    </div>
  );
}

// ---------- Annex G: Quality / audits ----------
function AnnexG({ psmf, onAnnexChange }: { psmf: Psmf; onAnnexChange: Updater }) {
  const g = psmf.annex.G;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Audit schedule (rolling 5-year plan)</CardTitle></CardHeader>
        <CardContent>
          <Textarea
            rows={4}
            value={g.schedule}
            onChange={(e) => onAnnexChange('G', (p) => ({ ...(p as typeof g), schedule: e.target.value }), 'Annex G — schedule')}
            placeholder="Describe the risk-based audit programme and planned audits over the next 5 years."
          />
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Audits conducted (last 5 years)</CardTitle>
          <Button
            size="sm"
            onClick={() => onAnnexChange('G', (p) => {
              const cur = p as typeof g;
              return { ...cur, audits: [...cur.audits, { id: newRowId(), scope: '', dateOfConduct: '', dateOfReport: '', significantFindings: '', capaStatus: 'open', resolutionDate: '' }] };
            }, 'Annex G — added audit')}
          >
            <Plus className="mr-1 h-4 w-4" /> Add audit
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Scope</TableHead>
                <TableHead>Conducted</TableHead>
                <TableHead>Report</TableHead>
                <TableHead>Significant findings</TableHead>
                <TableHead>CAPA</TableHead>
                <TableHead>Resolution</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {g.audits.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground">No audits listed.</TableCell></TableRow>
              )}
              {g.audits.map((a, idx) => (
                <TableRow key={a.id}>
                  <TableCell><Input value={a.scope} onChange={(e) => onAnnexChange('G', (p) => { const cur = p as typeof g; const next = [...cur.audits]; next[idx] = { ...a, scope: e.target.value }; return { ...cur, audits: next }; }, 'Annex G — audit scope')} /></TableCell>
                  <TableCell><Input type="date" value={a.dateOfConduct} onChange={(e) => onAnnexChange('G', (p) => { const cur = p as typeof g; const next = [...cur.audits]; next[idx] = { ...a, dateOfConduct: e.target.value }; return { ...cur, audits: next }; }, 'Annex G — date of conduct')} /></TableCell>
                  <TableCell><Input type="date" value={a.dateOfReport} onChange={(e) => onAnnexChange('G', (p) => { const cur = p as typeof g; const next = [...cur.audits]; next[idx] = { ...a, dateOfReport: e.target.value }; return { ...cur, audits: next }; }, 'Annex G — date of report')} /></TableCell>
                  <TableCell><Input value={a.significantFindings} onChange={(e) => onAnnexChange('G', (p) => { const cur = p as typeof g; const next = [...cur.audits]; next[idx] = { ...a, significantFindings: e.target.value }; return { ...cur, audits: next }; }, 'Annex G — significant findings')} /></TableCell>
                  <TableCell>
                    <select
                      value={a.capaStatus}
                      onChange={(e) => onAnnexChange('G', (p) => { const cur = p as typeof g; const next = [...cur.audits]; next[idx] = { ...a, capaStatus: e.target.value }; return { ...cur, audits: next }; }, 'Annex G — CAPA status')}
                      className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                    >
                      <option>open</option>
                      <option>in progress</option>
                      <option>closed</option>
                      <option>not applicable</option>
                    </select>
                  </TableCell>
                  <TableCell><Input type="date" value={a.resolutionDate} onChange={(e) => onAnnexChange('G', (p) => { const cur = p as typeof g; const next = [...cur.audits]; next[idx] = { ...a, resolutionDate: e.target.value }; return { ...cur, audits: next }; }, 'Annex G — resolution date')} /></TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => onAnnexChange('G', (p) => { const cur = p as typeof g; return { ...cur, audits: cur.audits.filter((x) => x.id !== a.id) }; }, 'Annex G — removed audit')}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Annex H: Products ----------
function AnnexH({ psmf, onAnnexChange }: { psmf: Psmf; onAnnexChange: Updater }) {
  const h = psmf.annex.H;
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Medicinal products</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">Grouped per active substance (INN).</p>
        </div>
        <Button
          size="sm"
          onClick={() => onAnnexChange('H', (p) => {
            const cur = p as typeof h;
            return { ...cur, products: [...cur.products, { id: newRowId(), activeSubstance: '', inn: '', authorisationNumber: '', procedureType: 'Centralised', rapporteurOrRms: '', marketingStatus: 'marketed' }] };
          }, 'Annex H — added product')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add product
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Active substance</TableHead>
              <TableHead>INN</TableHead>
              <TableHead>Auth. number</TableHead>
              <TableHead>Procedure</TableHead>
              <TableHead>Rapporteur / RMS</TableHead>
              <TableHead>Marketing</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {h.products.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground">No products listed.</TableCell></TableRow>
            )}
            {h.products.map((pr, idx) => (
              <TableRow key={pr.id}>
                <TableCell><Input value={pr.activeSubstance} onChange={(e) => onAnnexChange('H', (p) => { const cur = p as typeof h; const next = [...cur.products]; next[idx] = { ...pr, activeSubstance: e.target.value }; return { ...cur, products: next }; }, 'Annex H — active substance')} /></TableCell>
                <TableCell><Input value={pr.inn} onChange={(e) => onAnnexChange('H', (p) => { const cur = p as typeof h; const next = [...cur.products]; next[idx] = { ...pr, inn: e.target.value }; return { ...cur, products: next }; }, 'Annex H — INN')} /></TableCell>
                <TableCell><Input value={pr.authorisationNumber} onChange={(e) => onAnnexChange('H', (p) => { const cur = p as typeof h; const next = [...cur.products]; next[idx] = { ...pr, authorisationNumber: e.target.value }; return { ...cur, products: next }; }, 'Annex H — auth number')} /></TableCell>
                <TableCell>
                  <select
                    value={pr.procedureType}
                    onChange={(e) => onAnnexChange('H', (p) => { const cur = p as typeof h; const next = [...cur.products]; next[idx] = { ...pr, procedureType: e.target.value }; return { ...cur, products: next }; }, 'Annex H — procedure')}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option>Centralised</option>
                    <option>MRP</option>
                    <option>DCP</option>
                    <option>National</option>
                  </select>
                </TableCell>
                <TableCell><Input value={pr.rapporteurOrRms} onChange={(e) => onAnnexChange('H', (p) => { const cur = p as typeof h; const next = [...cur.products]; next[idx] = { ...pr, rapporteurOrRms: e.target.value }; return { ...cur, products: next }; }, 'Annex H — rapporteur/RMS')} /></TableCell>
                <TableCell>
                  <select
                    value={pr.marketingStatus}
                    onChange={(e) => onAnnexChange('H', (p) => { const cur = p as typeof h; const next = [...cur.products]; next[idx] = { ...pr, marketingStatus: e.target.value }; return { ...cur, products: next }; }, 'Annex H — marketing status')}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option>marketed</option>
                    <option>not-marketed</option>
                    <option>withdrawn</option>
                  </select>
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => onAnnexChange('H', (p) => { const cur = p as typeof h; return { ...cur, products: cur.products.filter((x) => x.id !== pr.id) }; }, 'Annex H — removed product')}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ---------- Annex I: Per-annex logbook ----------
function AnnexI({ psmf, onAnnexChange }: { psmf: Psmf; onAnnexChange: Updater }) {
  const i = psmf.annex.I;
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Document and record control</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Per-annex change history indexed A-H. Top-level logbook appears on the main page.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => onAnnexChange('I', (p) => {
            const cur = p as typeof i;
            return { ...cur, entries: [...cur.entries, { id: newRowId(), annex: 'A', date: new Date().toISOString().slice(0, 10), user: 'current-user', change: '' }] };
          }, 'Annex I — added entry')}
        >
          <Plus className="mr-1 h-4 w-4" /> Add entry
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Annex</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Change</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {i.entries.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">No entries listed.</TableCell></TableRow>
            )}
            {i.entries.map((e, idx) => (
              <TableRow key={e.id}>
                <TableCell>
                  <select
                    value={e.annex}
                    onChange={(ev) => onAnnexChange('I', (p) => { const cur = p as typeof i; const next = [...cur.entries]; next[idx] = { ...e, annex: ev.target.value }; return { ...cur, entries: next }; }, 'Annex I — entry annex')}
                    className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                  >
                    {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((x) => <option key={x}>{x}</option>)}
                  </select>
                </TableCell>
                <TableCell><Input type="date" value={e.date} onChange={(ev) => onAnnexChange('I', (p) => { const cur = p as typeof i; const next = [...cur.entries]; next[idx] = { ...e, date: ev.target.value }; return { ...cur, entries: next }; }, 'Annex I — entry date')} /></TableCell>
                <TableCell><Input value={e.user} onChange={(ev) => onAnnexChange('I', (p) => { const cur = p as typeof i; const next = [...cur.entries]; next[idx] = { ...e, user: ev.target.value }; return { ...cur, entries: next }; }, 'Annex I — entry user')} /></TableCell>
                <TableCell><Input value={e.change} onChange={(ev) => onAnnexChange('I', (p) => { const cur = p as typeof i; const next = [...cur.entries]; next[idx] = { ...e, change: ev.target.value }; return { ...cur, entries: next }; }, 'Annex I — entry change')} /></TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" onClick={() => onAnnexChange('I', (p) => { const cur = p as typeof i; return { ...cur, entries: cur.entries.filter((x) => x.id !== e.id) }; }, 'Annex I — removed entry')}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
